import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { Drawer, List, Box, Divider } from '@mui/material';
import { useWindowSize } from '../utils/resizeHook';
import { EmbersMessage } from '../message/message';
import { AppSwitcherSkeleton } from './appSwitcherSkeleton';
import { AppSwitcherItem } from './appSwitcherItem';
import { appSwitcherTheme } from './appSwitcherStyles';
import { EmbersInnerThemeProvider } from '../rtlProvider';
export var LAUNCH_STYLE = /*#__PURE__*/function (LAUNCH_STYLE) {
  LAUNCH_STYLE["SAME_TAB_ONLY"] = "SAME_TAB_ONLY";
  LAUNCH_STYLE["NEW_TAB_ONLY"] = "NEW_TAB_ONLY";
  LAUNCH_STYLE["SAME_TAB_WITH_NEW_TAB_OPTIONAL"] = "SAME_TAB_WITH_NEW_TAB_OPTIONAL";
  return LAUNCH_STYLE;
}({});
export var APP_MENU_ARIA_ID = 'app-menu';
export var AppSwitcher = function AppSwitcher(props) {
  var loading = props.loading,
    open = props.open,
    setOpen = props.setOpen,
    title = props.title,
    className = props.className,
    items = props.items,
    _onClick = props.onClick,
    current = props.current,
    extraItems = props.extraItems,
    extraTitle = props.extraTitle,
    error = props.error,
    onExtraItemClick = props.onExtraItemClick,
    _props$drawerWidth = props.drawerWidth,
    drawerWidth = _props$drawerWidth === void 0 ? 308 : _props$drawerWidth,
    disablePortal = props.disablePortal;
  var intl = useIntl();
  var SwitcherRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasScroll = _useState2[0],
    setHasScroll = _useState2[1];
  var _useState3 = useState(drawerWidth),
    _useState4 = _slicedToArray(_useState3, 2),
    newWidth = _useState4[0],
    setNewWidth = _useState4[1];
  var size = useWindowSize();

  // update on window height change (size = [width, height])
  useEffect(function () {
    var root = SwitcherRef === null || SwitcherRef === void 0 ? void 0 : SwitcherRef.current;
    if (root && root.scrollHeight > root.clientHeight) {
      !hasScroll && setHasScroll(true);
      /* 12px is the width of the scrollbar (see embersReset.css) */
      setNewWidth(drawerWidth + 12);
    } else {
      setNewWidth(drawerWidth);
    }
  }, [size[1]]);
  var renderEmpty = function renderEmpty() {
    return /*#__PURE__*/React.createElement("div", {
      className: "EmbersAppSwitcher-empty"
    }, /*#__PURE__*/React.createElement(EmbersMessage, {
      type: "destructiveSuccess",
      iconName: "icon-notification",
      closeIconAriaLabel: intl.formatMessage({
        id: 'embers.statusIcon.warn',
        defaultMessage: 'warning'
      })
    }, intl.formatMessage({
      id: 'embers.appSwitcher.empty',
      defaultMessage: 'Contact your administrator to gain access to apps.'
    })));
  };
  var renderList = function renderList() {
    if (!items) return;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(List, {
      id: APP_MENU_ARIA_ID
    }, items.map(function (item, index) {
      return /*#__PURE__*/React.createElement(AppSwitcherItem, {
        id: "EmbersAppSwitcher-item-".concat(item.id || item.name),
        disabled: item.disabled,
        isCurrent: (item.id || item.name) === current,
        key: item.name,
        name: item.name,
        icon: item.icon,
        route: item.route,
        onClick: function onClick(opensInNewTab) {
          _onClick && _onClick(item, opensInNewTab, index);
          setOpen && setOpen(false);
        },
        launchStyle: item.launchStyle
      });
    })), extraItems && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement("div", {
      className: "EmbersAppSwitcher-extraTitle"
    }, extraTitle || intl.formatMessage({
      id: 'embers.appSwitcher.extraTitle',
      defaultMessage: 'PARTNER APPS'
    })), /*#__PURE__*/React.createElement(List, {
      className: "EmbersAppSwitcher-extraList"
    }, extraItems.map(function (item, index) {
      return /*#__PURE__*/React.createElement(AppSwitcherItem, {
        id: "EmbersAppSwitcher-item-".concat(item.id || item.name),
        isCurrent: (item.id || item.name) === current,
        key: item.id || item.name,
        name: item.name,
        icon: item.icon,
        onClick: function onClick(opensInNewTab) {
          onExtraItemClick ? onExtraItemClick(item, opensInNewTab, index + items.length) : _onClick && _onClick(item, opensInNewTab, index + items.length);
          setOpen && setOpen(false);
        },
        launchStyle: item.launchStyle,
        route: item.route
      });
    }))));
  };
  var renderError = function renderError() {
    return /*#__PURE__*/React.createElement("div", {
      className: "EmbersAppSwitcher-error"
    }, /*#__PURE__*/React.createElement(EmbersMessage, {
      type: "error"
    }, intl.formatMessage({
      id: 'embers.appSwitcher.error',
      defaultMessage: 'We had a technical issue displaying your apps. Try reloading this page.'
    })));
  };
  var content;
  if (loading) {
    content = /*#__PURE__*/React.createElement(AppSwitcherSkeleton, null);
  } else if (error) {
    content = renderError();
  } else if (!(items !== null && items !== void 0 && items.length)) {
    content = renderEmpty();
  } else {
    content = /*#__PURE__*/React.createElement(Box, {
      ref: SwitcherRef,
      sx: {
        overflow: 'auto'
      }
    }, renderList());
  }
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: appSwitcherTheme
  }, /*#__PURE__*/React.createElement(Drawer, {
    disablePortal: disablePortal,
    sx: {
      width: newWidth,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: newWidth,
        boxSizing: 'border-box'
      }
    },
    open: open,
    onClose: function onClose() {
      return setOpen && setOpen(false);
    },
    className: classnames('EmbersAppSwitcher-root', {
      'EmbersAppSwitcher-error': error,
      'EmbersAppSwitcher-withScroll': hasScroll
    }, className),
    ModalProps: {
      keepMounted: true
    }
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h2", {
    className: "EmbersAppSwitcher-title"
  }, title || intl.formatMessage({
    id: 'embers.appSwitcher.title',
    defaultMessage: 'Apps'
  })), content)));
};